/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/BitString.ts
 * @create:      2018-08-30 16:07:15.084
 * @modify:      2025-07-08 17:09:49.280
 * @version:     6.0.0
 * @times:       117
 * @lines:       607
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_Empty } from "../const/Strings";
import { ReplaceAll } from "../string/ReplaceAll";
import { ReplaceAt } from "../string/ReplaceAt";
import { Reverse } from "../string/Reverse";
import { SplitByN } from "../string/SplitByN";
import { TrimStart } from "../string/TrimStart";
import { TBitStringType } from "../type/advanced";

/**
 * Represents a string of bits and provides various bitwise operations and utilities.
 *
 * The `BitString` class allows for the creation, manipulation, and conversion of bit strings.
 * It supports initialization from numbers, strings (binary or hexadecimal), and provides methods
 * for bitwise operations (AND, OR, XOR, NOT), shifting, rotating, reflecting, and conversion to numbers or byte arrays.
 *
 * @example
 * ```typescript
 * const bits = new BitString(10); // "1010"
 * bits.SetBit(1, true); // Set the least significant bit
 * const sum = bits.Add(new BitString(5));
 * ```
 *
 * @remarks
 * - Bit offsets are counted from right to left, starting at 1.
 * - Supports both binary and hexadecimal string initialization.
 * - Provides static helpers for 2D array initialization and byte conversion.
 *
 * @public
 * 
 * @version 1.0.0
 * @since 1.0.5
 */
export class BitString {
    /**
     * Gets the length of the binary string.
     * @returns The number of characters in the binary string.
     */
    get Length(): number {
        return this.binStr.length;
    }

    /**
     * Gets the hexadecimal string representation of the bit string.
     * 
     * @returns {string} The hexadecimal representation of the current bit string.
     */
    public get HEX(): string {
        return this.toString();
    }

    /**
     * Creates a new `BitString` instance from one or more byte values.
     *
     * Each byte is converted to its 8-bit binary representation and concatenated
     * to form the complete bit string.
     *
     * @param bytes - One or more numbers representing bytes (0-255) to convert.
     * @returns A new `BitString` instance representing the concatenated bits of the input bytes.
     */
    public static FromBytes(...bytes: number[]): BitString {
        let bin = Strings_Empty;
        for (const byte of bytes) {
            bin += byte.toString(2).padStart(8, "0");
        }

        return new BitString(bin);
    }

    /**
     * Initializes a 2D array of `BitString` instances with the specified dimensions.
     *
     * @param x - The number of rows in the 2D array.
     * @param y - The number of columns in each row of the 2D array.
     * @returns A 2D array (`BitString[][]`) where each element is a new instance of `BitString`.
     */
    public static Initialize2d(x: number, y: number): BitString[][] {
        return new Array<BitString>(x).fill(new BitString()).map(() => new Array<BitString>(y).fill(new BitString()));
    }

    private binStr: string;

    public constructor();
    public constructor(initialValue: number);
    public constructor(initialValue: number, type: TBitStringType);
    public constructor(initialValue: string);
    public constructor(initialValue: string, radix: number);
    public constructor(initialValue: string, type: TBitStringType);
    public constructor(
        initialValue?: string | number,
        type?: TBitStringType | number,
    ) {
        if (initialValue === undefined) {
            this.binStr = Strings_Empty;

            return;
        }

        if (typeof initialValue === "number") {
            if (initialValue > Number.MAX_SAFE_INTEGER) {
                throw new Error(`Initial value is too big: ${initialValue} > ${Number.MAX_SAFE_INTEGER}`);
            }

            this.binStr = initialValue.toString(2);
        } else {
            initialValue = ReplaceAll(initialValue, " ", "");

            let radix: number | undefined;

            if (typeof type === "number") {
                radix = type;
            } else {
                switch (type) {
                    case undefined:
                    case "bin":
                        radix = 2;
                        break;

                    case "hex":
                        radix = 16;
                        break;

                    default:
                        throw new Error(`Invalid BitStringType: ${type}`);
                }
            }

            if (radix === 2) {
                this.binStr = initialValue;
            } else if (radix === 16) {
                let temp: string = Strings_Empty;
                for (const val of initialValue) {
                    temp += parseInt(val, radix).toString(2).padStart(4, "0");
                }

                this.binStr = temp;
            } else {
                // to be Refined
                this.binStr = parseInt(initialValue, radix).toString(2);
            }
        }
    }

    /**
     * Sets the bit at the specified offset to the given boolean value.
     *
     * The offset is 1-based, where 1 refers to the least significant bit (rightmost).
     * If the offset exceeds the current length, the bit string is left-padded with zeros as needed.
     *
     * @param offset - The 1-based position of the bit to set. Must be greater than or equal to 1.
     * @param val - The boolean value to set the bit to (`true` for '1', `false` for '0').
     * @returns The updated BitString instance.
     * @throws {Error} If the offset is less than 1.
     */
    public SetBit(offset: number, val: boolean): this {
        if (offset < 1) {
            throw new Error(`Unexpected offset: ${offset}`);
        }

        const value = val ? "1" : "0";

        const index = this.Length - offset;
        if (index >= 0) {
            this.binStr = ReplaceAt(this.binStr, index, value);
        } else {
            this.binStr = value + this.binStr.padStart(offset - 1, "0");
        }

        return this;
    }

    /**
     * Retrieves the value of the bit at the specified offset from the end of the bit string.
     *
     * @param offset - The 1-based position from the end of the bit string to retrieve the bit from. Must be greater than or equal to 1.
     * @param defaultVal - The value to return if the offset is out of bounds. Defaults to `false`.
     * @returns `true` if the bit at the specified offset is '1', `false` if it is '0', or `defaultVal` if the offset is out of bounds.
     * @throws {Error} If the provided offset is less than 1.
     * 
     * @since 1.1.20
     * @version 2019-04-01
     */
    public GetBit(offset: number, defaultVal = false): boolean {
        if (offset < 1) {
            throw new Error(`Unexpected offset: ${offset}`);
        }

        const index = this.Length - offset;
        if (index < 0) {
            return defaultVal;
        }

        return this.binStr[index] === "1";
    }

    /**
     * Checks if any bit in the binary string is set to '1'.
     *
     * @returns {boolean} `true` if at least one bit is '1'; otherwise, `false`.
     */
    public AnyBit(): boolean {
        return this.binStr.includes("1");
    }

    /**
     * Converts the bit string to its string representation in the specified format.
     *
     * @param type - The output format for the bit string. Can be `BitStringType.BIN` for binary or `BitStringType.HEX` for hexadecimal. Defaults to `BitStringType.HEX`.
     * @param trim - If `true`, leading zeros are trimmed from the hexadecimal output. Defaults to `false`.
     * @returns The string representation of the bit string in the specified format. Returns `"0"` if the bit string is empty or consists only of zeros.
     * @throws {Error} If an invalid `BitStringType` is provided.
     */
    public toString(type: TBitStringType = "hex", trim = false): string {
        if (this.binStr.length === 0) {
            return Strings_Empty;
        }

        switch (type) {
            case "bin":
                return this.binStr;

            case "hex": {
                const array = SplitByN(this.binStr, 4);
                let hex = array.map(p => parseInt(p, 2).toString(16)).join(Strings_Empty);

                if (trim) {
                    hex = TrimStart(hex, "0", hex.length);
                }

                if (hex === Strings_Empty) {
                    return "0";
                }

                return hex;
            }

            default:
                throw new Error(`Invalid BitStringType: ${type}`);
        }
    }

    /**
     * Adds the current BitString to another BitString and returns the result as a new BitString.
     * The addition is performed bit by bit, starting from the least significant bit (rightmost),
     * and handles carry-over similar to binary addition.
     *
     * @param val - The BitString to add to the current BitString.
     * @returns A new BitString representing the sum of the two BitStrings.
     * 
     * @since 1.1.20
     * @version 2019-04-01
     */
    public Add(val: BitString): BitString {
        let result = Strings_Empty;
        let carry = false;
        let i = 1;
        let j = 1;
        while (i <= this.Length || j <= val.Length || carry) {
            const a = this.GetBit(i);
            const b = val.GetBit(j);

            if (carry) {
                if (a === b) {
                    result = "1" + result;
                    carry = a;
                } else {
                    result = "0" + result;
                    carry = true;
                }
            } else {
                if (a === b) {
                    result = "0" + result;
                    carry = a;
                } else {
                    result = "1" + result;
                    carry = false;
                }
            }

            // Move to next digits
            i++;
            j++;
        }

        return new BitString(result);
    }

    /**
     * Performs a bitwise AND operation between this BitString and another BitString or number.
     * 
     * If the input is a number, it is first converted to a BitString. The lengths of both BitStrings
     * are adjusted to match the longer one by padding with zeros if necessary. The result is a new
     * BitString where each bit is the logical AND of the corresponding bits in the two operands.
     * 
     * @param val - The BitString or number to perform the AND operation with.
     * @returns A new BitString representing the result of the bitwise AND operation.
     * 
     * @since 1.1.20
     * @version 2019-04-01
     */
    public AND(val: BitString | number): BitString {
        if (typeof val === "number") {
            val = new BitString(val);
        }

        if (this.Length > val.Length) {
            val.SetLength(this.Length);
        } else if (this.Length < val.Length) {
            this.SetLength(val.Length);
        }

        let result = Strings_Empty;

        for (let i = 0; i < this.Length; i++) {
            result += ((this.binStr[i] === "1" && val.binStr[i]) === "1" ? "1" : "0");
        }

        return new BitString(result);
    }

    /**
     * Performs a bitwise OR operation between this BitString and another BitString or number.
     * 
     * If a number is provided, it is first converted to a BitString. The operation is performed
     * bit by bit, starting from the least significant bit. The resulting BitString will have a length
     * equal to the longer of the two operands.
     * 
     * @param val - The BitString or number to OR with this BitString.
     * @returns A new BitString representing the result of the bitwise OR operation.
     * 
     * @since 1.1.20
     * @version 2019-04-01
     */
    public OR(val: BitString | number): BitString {
        if (typeof val === "number") {
            val = new BitString(val);
        }

        let result = Strings_Empty;
        let i = 1;

        while (i <= this.Length || i <= val.Length) {
            const a = this.GetBit(i);
            const b = val.GetBit(i);

            if (a || b) {
                result = "1" + result;
            } else {
                result = "0" + result;
            }

            i++;
        }

        return new BitString(result);
    }

    /**
     * Returns a new BitString instance with each bit inverted.
     * 
     * Iterates through the current binary string and flips each bit:
     * - '0' becomes '1'
     * - '1' becomes '0'
     *
     * @returns {BitString} A new BitString with all bits inverted.
     * 
     * @since 1.1.20
     * @version 2019-04-01
     */
    public NOT(): BitString {
        let result = Strings_Empty;

        for (const c of this.binStr) {
            result += c === "0" ? "1" : "0";
        }

        return new BitString(result);
    }

    /**
     * Performs a bitwise XOR operation between this BitString and another BitString or number.
     *
     * If the input value is a number, it is first converted to a BitString.
     * The lengths of both BitStrings are adjusted to match by padding with leading zeros if necessary.
     * Returns a new BitString representing the result of the XOR operation.
     *
     * @param val - The BitString or number to XOR with this BitString.
     * @returns A new BitString containing the result of the XOR operation.
     * 
     * @since 1.1.20
     * @version 2019-04-01
     */
    public XOR(val: BitString | number): BitString {
        if (typeof val === "number") {
            val = new BitString(val);
        }

        if (this.Length > val.Length) {
            val.SetLength(this.Length);
        } else if (this.Length < val.Length) {
            this.SetLength(val.Length);
        }

        let result = Strings_Empty;

        for (let i = 0; i < this.Length; i++) {
            result += this.binStr[i] !== val.binStr[i] ? "1" : "0";
        }

        return new BitString(result);
    }

    /**
     * Shifts the current bit string to the left by the specified number of positions.
     * This operation appends `n` zeros to the right end of the bit string.
     *
     * @param n - The number of positions to shift left (number of zeros to append).
     * @returns A new `BitString` instance with the bits shifted left by `n` positions.
     */
    public LeftShift(n: number): BitString {
        const right = "0".repeat(n);
        return new BitString(this.binStr + right);
    }

    /**
     * Performs a right shift operation on the current BitString by the specified number of bits.
     * Pads the left side with zeros and truncates bits from the right.
     *
     * @param n - The number of bits to shift to the right.
     * @returns A new BitString instance representing the shifted value.
     */
    public RightShift(n: number): BitString {
        const left = "0".repeat(n);
        if (this.Length < n) {
            return new BitString(left);
        } else {
            return new BitString(left + this.binStr.substring(0, this.Length - n));
        }
    }

    /**
     * Performs a left rotation (circular shift) of the bit string by the specified number of positions.
     * 
     * @param n - The number of positions to rotate the bit string to the left.
     * @param bits - The total number of bits to consider for the rotation. Defaults to 64.
     * @returns A new `BitString` instance with the bits rotated to the left by `n` positions.
     * 
     * @version 1.0.0
     * @version 2025-06-27
     */
    public LeftRotate(n: number, bits = 64): BitString {
        this.SetLength(bits);

        n = ((n % bits) + bits) % bits;

        // return this.LeftShift(n).OR(this.RightShift(bits - n)).SetLength(bits);
        return new BitString(this.binStr.substring(n) + this.binStr.substring(0, n));
    }

    /**
     * Rotates the bits of the current BitString instance to the right by the specified number of positions.
     *
     * @param n - The number of positions to rotate the bits to the right.
     * @param bits - The total number of bits to consider for the rotation. Defaults to 64.
     * @returns The BitString instance after performing the right rotation.
     * 
     * @version 1.0.0
     * @version 2025-06-27
     */
    public RightRotate(n: number, bits = 64): BitString {
        // this.SetLength(bits);
        // return this.RightShift(n).OR(this.LeftShift(bits - n)).SetLength(bits);
        return this.LeftRotate(bits - n, bits);
    }

    /**
     * Converts the internal binary string representation (`binStr`) to a number.
     *
     * @returns The numeric value of the binary string.
     */
    public ToNumber(): number {
        return parseInt(this.binStr, 2);
    }

    /**
     * Converts the bit string to an array of bytes.
     *
     * @param bits - Optional. If provided, sets the length of the bit string to the specified number of bits before conversion.
     * @returns An array of numbers, where each number represents a byte from the bit string.
     */
    public ToBytes(bits?: number): number[] {
        if (bits) {
            this.SetLength(bits);
        }

        const byteArray: number[] = [];

        const hexArray = SplitByN(this.HEX, 2);
        for (const hex of hexArray) {
            byteArray.push(parseInt(hex, 16));
        }

        return byteArray;
    }

    /**
     * Returns a new `BitString` instance with the bits in reverse order.
     * Optionally sets the length of the bit string before reflecting.
     *
     * @param bits - Optional. If provided, sets the length of the bit string before reflecting.
     * @returns A new `BitString` with the bits reversed.
     */
    public Reflect(bits?: number): BitString {
        if (bits) {
            this.SetLength(bits);
        }

        return new BitString(Reverse(this.binStr));
    }

    /**
     * Sets the length of the bit string to the specified number of bits.
     * 
     * If the current length is greater than the specified length, the bit string is truncated from the left.
     * If the current length is less than the specified length, the bit string is left-padded with zeros.
     * 
     * @param bits - The desired length of the bit string in bits.
     * @returns The current instance for method chaining.
     */
    public SetLength(bits: number): this {
        if (this.Length > bits) {
            this.binStr = this.binStr.substring(this.Length - bits);
        } else if (this.Length < bits) {
            this.binStr = this.binStr.padStart(bits, "0");
        }

        return this;
    }

    /**
     * Creates and returns a new `BitString` instance that is a copy of the current object.
     *
     * @returns {BitString} A new `BitString` object with the same binary string as the original.
     */
    public Clone(): BitString {
        return new BitString(this.binStr);
    }

    /**
     * Reverses the order of bytes in the current bit string.
     *
     * @param bytes - The number of bytes to consider for swapping. Defaults to 8.
     * @returns A new `BitString` instance with the byte order reversed.
     *
     * @remarks
     * This method sets the bit string length to `bytes * 8`, splits the string into 8-bit chunks (bytes),
     * reverses their order, and joins them back together. The result is returned as a new `BitString`.
     */
    public Swap(bytes = 8) {
        this.SetLength(bytes * 8);
        const result = SplitByN(this.binStr, 8).reverse().join(Strings_Empty);
        return new BitString(result);
    }

    /**
     * Determines whether this BitString is equal to another BitString,
     * ignoring leading zeros in their binary string representations.
     *
     * @param another - The BitString instance to compare with.
     * @returns `true` if the normalized binary strings are equal; otherwise, `false`.
     */
    public Equals(another: BitString): boolean {
        return TrimStart(this.binStr, "0") === TrimStart(another.binStr, "0");
    }
}
